import { useQuery } from '@tanstack/react-query';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import { useAppContainerContext } from 'src/UI/app-container';
import { ChatMode } from '../chat/types';
import { SiestaAction, SiestaActionType } from '../types';
import { useClients } from 'src/clients/hooks/useClients';

type ChatAction = Extract<SiestaAction, { type: typeof SiestaActionType.CHAT }>;

type SiestaActionsConfig = {
  [pageType: string]: ChatAction[];
};

const SIESTA_ACTIONS_CONFIG: SiestaActionsConfig = {
  'suitability-report-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'capacity-for-loss',
      label:
        "Explain the client's capacity for loss based on their current investment assets and cashflow",
    },
    {
      type: SiestaActionType.CHAT,
      id: 'suitability-rationale',
      label: 'Strengthen the suitability rationale for the recommended funds',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'cashflow-narrative',
      label: 'Add more narrative linking cashflows to objectives',
    },
  ],
  'data-export-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'remove-empty-rows-columns',
      label: 'Remove empty rows and columns from tables to improve readability',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'consolidate-sparse-sections',
      label: 'Consolidate sparse sections with few entries into a cleaner layout',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'replace-placeholders',
      label: 'Replace placeholder values with a consistent format',
    },
  ],
  'meeting-agenda-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'add-action-points',
      label: 'Add action points and next steps for each agenda topic',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'add-client-data-references',
      label: 'Include specific client data references to support discussion points',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'expand-objectives',
      label: 'Expand objectives section with measurable outcomes to review',
    },
  ],
};

type UseSuggestedActionsParams = {
  documentId?: string;
  documentContent?: string;
  clientId?: string;
  conversationId?: string;
};

export type GenerateSuggestionsResponse = {
  suggestions: string[];
};

export function useSuggestedActions(params?: UseSuggestedActionsParams): {
  actions: SiestaAction[];
  isLoading: boolean;
} {
  const { contentMeta } = useAppContainerContext();
  const pageType = contentMeta?.pageType;
  const isDocumentMode = contentMeta?.chatMode === ChatMode.DOCUMENT;

  const { clientId: contextClientId } = useCurrentClientIfAvailable();
  const { documentId, documentContent, clientId: paramClientId, conversationId } = params ?? {};
  const clientId = paramClientId ?? contextClientId;
  const { isLoading: isLoadingClients } = useClients();
  const canFetch = !!(documentId && documentContent && clientId && isDocumentMode);

  const { data, isLoading } = useQuery<GenerateSuggestionsResponse>({
    queryKey: ['suggested-actions', documentId],
    queryFn: async () => {
      const { data } = await api.post<GenerateSuggestionsResponse>(
        `documents/${documentId}/suggested-actions/generate`,
        { documentContent, clientId, conversationId },
      );
      return data;
    },
    enabled: canFetch,
    staleTime: Infinity,
  });

  const defaultActions: SiestaAction[] = clientId
    ? [
        {
          type: SiestaActionType.LINK,
          id: 'agenda',
          label: 'Meeting agenda',
          description: 'Includes client summary and topics for discussion',
          descriptionMode: 'hover',
          href: `/client/${clientId}/meeting-agenda`,
        },
        {
          type: SiestaActionType.LINK,
          id: 'meeting-note',
          label: 'Meeting note',
          description: 'Generate notes from meeting recordings',
          descriptionMode: 'hover',
          href: `/client/${clientId}/meeting-note`,
        },
        {
          type: SiestaActionType.LINK,
          id: 'data-export',
          label: 'Data export',
          description: "Export a data export populated with the client's data",
          descriptionMode: 'hover',
          href: `/client/${clientId}/data-export`,
        },
        {
          type: SiestaActionType.LINK,
          id: 'suitability-report',
          label: 'Suitability report',
          description:
            'Generate a suitability report for your client based on the information we currently hold',
          descriptionMode: 'hover',
          href: `/client/${clientId}/suitability-report`,
        },
      ]
    : [];

  if (data?.suggestions?.length) {
    return {
      actions: data.suggestions.map((text: string, i: number) => ({
        type: SiestaActionType.CHAT,
        id: `ai-${i}`,
        label: text,
      })),
      isLoading: false,
    };
  }

  const fallback =
    pageType && SIESTA_ACTIONS_CONFIG[pageType] ? SIESTA_ACTIONS_CONFIG[pageType] : defaultActions;

  return {
    actions: fallback,
    isLoading: (canFetch && isLoading) || (clientId == null && isLoadingClients),
  };
}
